import type { PricingEntry, PricingResponse, PricingVendor, SupportedEndpointInfo } from '@/api/pricing'

export type PricingDisplayType = 'USD' | 'CNY' | 'CUSTOM' | 'TOKENS'
export type TokenUnit = 'M' | 'K'

export interface NormalizedPricingData {
  models: PricingEntry[]
  vendors: PricingVendor[]
  vendorMap: Map<number, PricingVendor>
  groupRatio: Record<string, number>
  usableGroup: Record<string, string>
  supportedEndpointMap: Record<string, SupportedEndpointInfo>
  supportedEndpointOptions: string[]
  autoGroups: string[]
}

export interface PriceDisplayContext {
  displayType?: PricingDisplayType
  currency?: Exclude<PricingDisplayType, 'TOKENS'>
  tokenUnit?: TokenUnit
  priceRate?: number
  usdExchangeRate?: number
  customExchangeRate?: number
  customCurrencySymbol?: string
  showWithRecharge?: boolean
  precision?: number
}

export interface ModelPriceItem {
  key: string
  label: string
  value: string | number
  suffix?: string
}

type NullableModelPriceItem = Omit<ModelPriceItem, 'value'> & {
  value: string | number | null
}

export interface ModelPriceResult {
  isPerToken: boolean
  isTokensDisplay: boolean
  usedGroup: string
  usedGroupRatio: number
  items: ModelPriceItem[]
  primary: string
  secondary?: string
}

function isFiniteValue(value: unknown): value is number {
  return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
}

function formatRatio(value: unknown) {
  return isFiniteValue(value) ? Number(Number(value).toFixed(6)) : null
}

function formatCurrency(valueUSD: number, context: PriceDisplayContext) {
  const currency = context.currency || 'USD'
  const precision = context.precision ?? 4
  const priceRate = context.priceRate ?? 1
  const usdExchangeRate = context.usdExchangeRate ?? priceRate
  const customExchangeRate = context.customExchangeRate ?? 1
  let price = valueUSD

  if (context.showWithRecharge) {
    price = (valueUSD * priceRate) / usdExchangeRate
  }

  if (currency === 'CNY') return `¥${(price * usdExchangeRate).toFixed(precision)}`
  if (currency === 'CUSTOM') return `${context.customCurrencySymbol || '¤'}${(price * customExchangeRate).toFixed(precision)}`
  return `$${price.toFixed(precision)}`
}

export function normalizePricingResponse(response?: PricingResponse): NormalizedPricingData {
  const vendors = response?.vendors || []
  const vendorMap = new Map<number, PricingVendor>()
  for (const vendor of vendors) {
    vendorMap.set(vendor.id, vendor)
  }

  const models = [...(response?.data || [])].map((model) => {
    const vendor = model.vendor_id ? vendorMap.get(model.vendor_id) : undefined
    return {
      ...model,
      vendor_name: model.vendor_name || vendor?.name,
      vendor_icon: model.vendor_icon || vendor?.icon,
      vendor_description: model.vendor_description || vendor?.description,
      enable_groups: model.enable_groups || [],
      supported_endpoint_types: model.supported_endpoint_types || [],
    }
  })

  models.sort((a, b) => {
    const quotaCompare = (a.quota_type ?? 0) - (b.quota_type ?? 0)
    if (quotaCompare !== 0) return quotaCompare
    const aGpt = a.model_name.toLowerCase().startsWith('gpt')
    const bGpt = b.model_name.toLowerCase().startsWith('gpt')
    if (aGpt !== bGpt) return aGpt ? -1 : 1
    return a.model_name.localeCompare(b.model_name)
  })

  const supportedEndpointMap = response?.supported_endpoint || {}
  const supportedEndpointOptions = Object.keys(supportedEndpointMap).length
    ? Object.keys(supportedEndpointMap)
    : response?.supported_endpoints || []

  return {
    models,
    vendors,
    vendorMap,
    groupRatio: response?.group_ratio || {},
    usableGroup: response?.usable_group || {},
    supportedEndpointMap,
    supportedEndpointOptions,
    autoGroups: response?.auto_groups || [],
  }
}

export function pickPricingGroup(
  model: PricingEntry,
  selectedGroup: string,
  groupRatio: Record<string, number>
) {
  let usedGroup = selectedGroup
  let usedGroupRatio = groupRatio[selectedGroup]

  if (selectedGroup === 'all' || usedGroupRatio === undefined) {
    let minRatio = Number.POSITIVE_INFINITY
    for (const group of model.enable_groups || []) {
      const ratio = groupRatio[group]
      if (ratio !== undefined && ratio < minRatio) {
        minRatio = ratio
        usedGroup = group
        usedGroupRatio = ratio
      }
    }
  }

  return {
    usedGroup: usedGroup || 'all',
    usedGroupRatio: usedGroupRatio ?? 1,
  }
}

export function calculateModelPrice(
  model: PricingEntry,
  selectedGroup: string,
  groupRatio: Record<string, number>,
  context: PriceDisplayContext = {}
): ModelPriceResult {
  const { usedGroup, usedGroupRatio } = pickPricingGroup(model, selectedGroup, groupRatio)
  const displayType = context.displayType || 'USD'
  const tokenUnit = context.tokenUnit || 'M'
  const unitDivisor = tokenUnit === 'K' ? 1000 : 1
  const unitLabel = tokenUnit === 'K' ? 'K' : 'M'

  if (model.quota_type === 0) {
    if (displayType === 'TOKENS') {
      const rawItems: NullableModelPriceItem[] = [
        { key: 'input-ratio', label: '输入倍率', value: formatRatio(model.model_ratio), suffix: 'x' },
        { key: 'completion-ratio', label: '补全倍率', value: formatRatio(model.completion_ratio), suffix: 'x' },
        { key: 'cache-ratio', label: '缓存读取倍率', value: formatRatio(model.cache_ratio), suffix: 'x' },
        { key: 'create-cache-ratio', label: '缓存创建倍率', value: formatRatio(model.create_cache_ratio), suffix: 'x' },
        { key: 'image-ratio', label: '图片输入倍率', value: formatRatio(model.image_ratio), suffix: 'x' },
        { key: 'audio-input-ratio', label: '音频输入倍率', value: formatRatio(model.audio_ratio), suffix: 'x' },
        { key: 'audio-output-ratio', label: '音频补全倍率', value: formatRatio(model.audio_completion_ratio), suffix: 'x' },
      ]
      const items = rawItems.filter((item): item is ModelPriceItem => item.value !== null)

      return {
        isPerToken: true,
        isTokensDisplay: true,
        usedGroup,
        usedGroupRatio,
        items,
        primary: items[0] ? `${items[0].value}${items[0].suffix}` : '-',
        secondary: '按量计费',
      }
    }

    const inputBaseUSD = (model.model_ratio ?? 0) * 2 * usedGroupRatio
    const formatTokenPrice = (priceUSD: number) => {
      const formatted = formatCurrency(priceUSD, context)
      const symbol = formatted.replace(/[0-9.,-]/g, '')
      const numeric = Number(formatted.replace(/[^0-9.-]/g, '')) / unitDivisor
      return `${symbol}${numeric.toFixed(context.precision ?? 4)}`
    }

    const rawItems: Array<ModelPriceItem | null> = [
      { key: 'input', label: '输入价格', value: formatTokenPrice(inputBaseUSD), suffix: ` / 1${unitLabel} Tokens` },
      {
        key: 'completion',
        label: '补全价格',
        value: formatTokenPrice(inputBaseUSD * (model.completion_ratio ?? 1)),
        suffix: ` / 1${unitLabel} Tokens`,
      },
      isFiniteValue(model.cache_ratio)
        ? {
            key: 'cache',
            label: '缓存读取价格',
            value: formatTokenPrice(inputBaseUSD * Number(model.cache_ratio)),
            suffix: ` / 1${unitLabel} Tokens`,
          }
        : null,
      isFiniteValue(model.create_cache_ratio)
        ? {
            key: 'create-cache',
            label: '缓存创建价格',
            value: formatTokenPrice(inputBaseUSD * Number(model.create_cache_ratio)),
            suffix: ` / 1${unitLabel} Tokens`,
          }
        : null,
      isFiniteValue(model.image_ratio)
        ? {
            key: 'image',
            label: '图片输入价格',
            value: formatTokenPrice(inputBaseUSD * Number(model.image_ratio)),
            suffix: ` / 1${unitLabel} Tokens`,
          }
        : null,
      isFiniteValue(model.audio_ratio)
        ? {
            key: 'audio-input',
            label: '音频输入价格',
            value: formatTokenPrice(inputBaseUSD * Number(model.audio_ratio)),
            suffix: ` / 1${unitLabel} Tokens`,
          }
        : null,
      isFiniteValue(model.audio_ratio) && isFiniteValue(model.audio_completion_ratio)
        ? {
            key: 'audio-output',
            label: '音频补全价格',
            value: formatTokenPrice(inputBaseUSD * Number(model.audio_ratio) * Number(model.audio_completion_ratio)),
            suffix: ` / 1${unitLabel} Tokens`,
          }
        : null,
    ]
    const items = rawItems.filter((item): item is ModelPriceItem => Boolean(item))

    return {
      isPerToken: true,
      isTokensDisplay: false,
      usedGroup,
      usedGroupRatio,
      items,
      primary: items[0] ? `${items[0].value}${items[0].suffix}` : '-',
      secondary: items[1] ? `${items[1].label} ${items[1].value}` : undefined,
    }
  }

  const price = formatCurrency((model.model_price ?? 0) * usedGroupRatio, {
    ...context,
    precision: context.precision ?? 3,
  })
  const items = [{ key: 'fixed', label: '模型价格', value: price, suffix: ' / 次' }]

  return {
    isPerToken: false,
    isTokensDisplay: false,
    usedGroup,
    usedGroupRatio,
    items,
    primary: `${price} / 次`,
    secondary: '按次计费',
  }
}
